import { FC, useCallback } from 'react';
import ShowMoreIcon from 'icon/chevron_down_fill.svg';
import { useViewTracking } from '~/utils/use-view-tracking';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { $ArticleCommentDetail } from 'shared/atoms/article/article-comment-detail';
import { useRouteLoader } from '~/utils/use-route-loader';
import { $ArticleCommentDetailLoadmore } from 'shared/atoms/article/article-comment-detail-loadmore';
import { IPrimaryComment } from 'shared/models';
import { $CurrentArticleAsAvailable } from '@article/shared/atoms/current-article';
import { Loading } from '~/components/loading';
import { ModuleType } from '~/utils/module-type';
import { ClickPosition } from '~/utils/click-position';
import useReflowClick from '~/utils/use-reflow-click';
import { useActiveToCurrentArticle } from '@article/shared/utils/use-active-to-current-article';
import { $IsMobileUser } from '~/utils/is-mobile-user';

import './style.scss';

interface IRepliesSeeMoreProps {
  comment: IPrimaryComment;
}

export const RepliesSeeMore: FC<IRepliesSeeMoreProps> = (props) => {
  const { t } = useTranslation();
  const activeToCurrentArticle = useActiveToCurrentArticle();

  const isMobileUser = useAtomValue($IsMobileUser);
  const { comment } = props;
  const { commentId, replyCount } = comment;
  const {
    author: { userId },
  } = useAtomValue($CurrentArticleAsAvailable);
  const { hasMore, cursor } = useAtomValue($ArticleCommentDetail(commentId));

  const replyLoader = useRouteLoader(
    'comment-detail',
    {
      media_id: userId,
      comment_id: commentId,
      offset: cursor,
    },
    {
      atomFamilies: { $ArticleCommentDetailLoadmore },
    },
  );

  const seeMoreObservedRef = useViewTracking({
    onView: useCallback(() => {
      if (isMobileUser) {
        window.Tea('wap_cta_show', {
          current_page_module: ModuleType.comment,
          current_position: ClickPosition.comment_reply_see_more,
        });
      }
    }, []),
  });

  const handleReadMoreClick = useReflowClick(
    {
      defaultBehavior: () => {
        replyLoader.load();
      },
      reflowBehavior: () => {
        activeToCurrentArticle(ClickPosition.comment_reply_see_more);
      },
      clickEvent: () => ({
        current_position: ClickPosition.comment_reply_see_more,
      }),
    },
    [activeToCurrentArticle, replyLoader],
  );

  if (!hasMore) {
    return null;
  }

  if (replyLoader.state === 'loading') {
    return <Loading variant={'ball'} />;
  }

  return (
    <div ref={seeMoreObservedRef} className="replies-see-more" onClick={handleReadMoreClick}>
      <div className="line" />
      {`${t('seo_see_more_reply_comment')}(${String(replyCount - cursor)})`}
      <ShowMoreIcon className="chevron_down" />
    </div>
  );
};
